import React, {
  useEffect, useState, useMemo, useCallback, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Row,
  Alert,
  Form,
  Col,
  Button,
  useToggle,
} from '@openedx/paragon';
import { Search, Error } from '@openedx/paragon/icons';

import {
  getPlatforms,
  getPrograms,
  getSelectedCompanyOrPlatformKey,
  getFullCourseList,
  getLoadingStatuses as getStudioLoadingStatuses,
  getCompanyCatalog,
  getSavingStatuses,
  getCompanyManagementPortalGeneralData,
} from '../../data/selectors';
import {
  setSelectedCompanyOrPlatformKey,
} from '../../data/slice';
import {
  createProgramThunk,
  fetchProgramData,
  updateProgramThunk as updateProgramThunkImport,
  deleteProgramThunk,
  fetchCompanyCatalog,
} from '../../data/thunks';
import { LoadingSpinner } from '../../../generic/Loading';
import { RequestStatus } from '../../../data/constants';
import CourseListPicker from './course-list';
import TagsChips from './tags-chips';
import messages from '../messages';
import './index.scss';

interface Props {
  isLoading: boolean;
  isFailed: boolean;
}

interface Platform {
  key: string,
  org: string,
  name: string
}

interface Program {
  course_list: string[],
  item_id: string,
  name: string,
  enabled: boolean,
  slug: null,
  org: string,
  platform_key: string,
  item_type: string,
  description: string,
  tags: {
    meta: string[],
    topics: string[]
  },
  auto_enroll: boolean,
  auto_unenroll: boolean
}

const EMPTY_PROGRAM = {
  course_list: [],
  item_id: '',
  name: '',
  enabled: false,
  slug: null,
  org: '',
  platform_key: '',
  item_type: '',
  description: '',
  tags: {
    meta: [],
    topics: [],
  },
  auto_enroll: true,
  auto_unenroll: true,
};

const EMPTY_PLATFORM = { key: '', org: '', name: '' };

const ProgramsTab: React.FC<Props> = ({
  isLoading,
  isFailed,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const platforms = useSelector(getPlatforms);
  const programs = useSelector(getPrograms);
  const sharedSelectionKey = useSelector(getSelectedCompanyOrPlatformKey);
  const courses = useSelector(getFullCourseList);
  const { fullCourseListLoadingStatus, platformLoadingStatus } = useSelector(getStudioLoadingStatuses);
  const isLoadingCourses = fullCourseListLoadingStatus === RequestStatus.IN_PROGRESS;
  const isPlatformsLoaded = platformLoadingStatus === RequestStatus.SUCCESSFUL;
  const companyCatalog = useSelector(getCompanyCatalog);
  const savingStates = useSelector(getSavingStatuses);
  const allCompanies = useSelector(getCompanyManagementPortalGeneralData);
  const isCatalogLoading = savingStates.fetchCompanyCatalogStatus === RequestStatus.IN_PROGRESS;
  // An org truly has no catalog only when the fetch has completed and returned empty.
  // While loading we treat it as unknown to avoid a false-positive warning.
  const hasCatalog = isCatalogLoading ? null : !!(companyCatalog?.catalog);
  // Org has a catalog but no platform entry — pathway creation is blocked until
  // a dev manually creates the platform record via /api/sbl/platforms.
  const isMissingPlatform = hasCatalog === true && !platforms.some(p => p.key === sharedSelectionKey);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>(EMPTY_PLATFORM);
  const [isLegacyCompany, setIsLegacyCompany] = useState(false);
  const [legacyCompanyName, setLegacyCompanyName] = useState('');

  // Get company org from company catalog data (shared across tabs)
  const companyOrg = companyCatalog?.company?.edx_course_org || '';
  const companyName = selectedPlatform.name;

  /**
   * Resolve a human-readable display name for an org key.
   * Priority: real platform entry name → CMP company friendly_name → slug fallback.
   */
  const resolveOrgDisplayName = useCallback((orgKey: string): string => {
    const realPlatform = platforms.find((p: Platform) => p.key === orgKey);
    if (realPlatform?.name) { return realPlatform.name; }
    const cmpCompany = Array.isArray(allCompanies)
      ? allCompanies.find((c: any) => c.company_name === orgKey)
      : null;
    return cmpCompany?.friendly_name || orgKey;
  }, [platforms, allCompanies]);

  const [editPlatform, setEditPlatform] = useState<Platform>(EMPTY_PLATFORM);

  // Org selector search state
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const orgDropdownRef = useRef<HTMLDivElement | null>(null);

  // Pathway selector search state
  const [pathwaySearchQuery, setPathwaySearchQuery] = useState('');
  const [isPathwayDropdownOpen, setIsPathwayDropdownOpen] = useState(false);
  const pathwayDropdownRef = useRef<HTMLDivElement | null>(null);

  // Close org dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target as Node)) {
        setIsOrgDropdownOpen(false);
        setOrgSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close pathway dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pathwayDropdownRef.current && !pathwayDropdownRef.current.contains(e.target as Node)) {
        setIsPathwayDropdownOpen(false);
        setPathwaySearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Platforms filtered by org search query
  const filteredPlatforms = useMemo(() => {
    const visible = platforms.filter((p: Platform) => p.key !== 'main');
    if (!orgSearchQuery.trim()) {
      return visible;
    }
    const q = orgSearchQuery.toLowerCase();
    return visible.filter(
      (p: Platform) => p.name.toLowerCase().includes(q) || p.key.toLowerCase().includes(q),
    );
  }, [platforms, orgSearchQuery]);

  // Programs filtered by selected platform and pathway search query
  const filteredPrograms = useMemo(() => {
    let results = selectedPlatform.key
      ? programs.filter((p: Program) => p.platform_key === selectedPlatform.key)
      : programs;
    if (pathwaySearchQuery.trim()) {
      const q = pathwaySearchQuery.toLowerCase();
      results = results.filter((p: Program) => p.name.toLowerCase().includes(q));
    }
    return results;
  }, [programs, pathwaySearchQuery, selectedPlatform.key]);

  const [selectedProgram, setSelectedProgram] = useState<Program>(EMPTY_PROGRAM);
  const [newProgram, setNewProgram] = useState<Program>(EMPTY_PROGRAM);
  const [editProgram, setEditProgram] = useState<Program>(EMPTY_PROGRAM);
  const [isProgramDialogShown, showProgramDialog, hideProgramDialog] = useToggle(false);
  const [createMetaTagInput, setCreateMetaTagInput] = useState('');
  const [createTopicsTagInput, setCreateTopicsTagInput] = useState('');
  const [editMetaTagInput, setEditMetaTagInput] = useState('');
  const [editTopicsTagInput, setEditTopicsTagInput] = useState('');
  const [isSubmittingNewProgram, setIsSubmittingNewProgram] = useState(false);
  const [newProgramError, setNewProgramError] = useState<string | null>(null);
  const [isUpdatingProgram, setIsUpdatingProgram] = useState(false);
  const [updateProgramError, setUpdateProgramError] = useState<string | null>(null);
  const [isDeletingProgram, setIsDeletingProgram] = useState(false);
  const [deleteProgramError, setDeleteProgramError] = useState<string | null>(null);

  const programProps = () => {
    if (filteredPrograms.length === 0) {
      return 'No pathways available';
    }
    if (selectedProgram.name) {
      return selectedProgram.name;
    }

    return 'Select a pathway';
  };

  // Fetch programs when selectedPlatform changes
  useEffect(() => {
    if (selectedPlatform.key) {
      dispatch(fetchProgramData(selectedPlatform.key));
    }
  }, [selectedPlatform.key, selectedPlatform.org, dispatch]);

  // Sync editProgram when selectedProgram changes
  useEffect(() => {
    if (selectedProgram.item_id) {
      setEditProgram(selectedProgram);
    }
  }, [selectedProgram]);

  // Sync editPlatform when selectedPlatform changes
  useEffect(() => {
    if (selectedPlatform.key) {
      setEditPlatform(selectedPlatform);
    }
  }, [selectedPlatform]);

  // On mount or when platforms load, check if there's a shared selection to restore
  useEffect(() => {
    if (sharedSelectionKey && !selectedPlatform.key) {
      const matchingPlatform = platforms.find(p => p.key === sharedSelectionKey);
      if (matchingPlatform) {
        setSelectedPlatform(matchingPlatform);
        setIsLegacyCompany(false);
        setLegacyCompanyName('');
      } else if (hasCatalog === true && isPlatformsLoaded) {
        // Org has a catalog but no platform entry yet (no pathways created).
        // Build a synthetic platform from catalog data so the org shows as selected.
        const matchingCompany = Array.isArray(allCompanies)
          ? allCompanies.find((c: any) => c.company_name === sharedSelectionKey)
          : null;
        setSelectedPlatform({
          key: sharedSelectionKey,
          org: matchingCompany?.edx_course_org || sharedSelectionKey,
          name: resolveOrgDisplayName(sharedSelectionKey),
        });
        setIsLegacyCompany(false);
        setLegacyCompanyName('');
      } else if (hasCatalog === false) {
        // No platform AND catalog confirmed absent — truly a legacy company.
        setIsLegacyCompany(true);
        setLegacyCompanyName(sharedSelectionKey);
      }
      // hasCatalog === null: catalog still loading. Wait for next render.
    }
  }, [sharedSelectionKey, platforms.length, hasCatalog, isPlatformsLoaded, resolveOrgDisplayName, allCompanies]);

  // Sync changes from other tabs (e.g., CMP tab).
  // Only depends on sharedSelectionKey — we do NOT read hasCatalog here because
  // it may still hold the previous org's value while the new fetch is in flight,
  // which would cause a stale-catalog false-positive (e.g. legacy org treated as
  // catalog org). The mount/restore effect below handles hasCatalog resolution.
  useEffect(() => {
    if (sharedSelectionKey && selectedPlatform.key !== sharedSelectionKey) {
      const matchingPlatform = platforms.find(p => p.key === sharedSelectionKey);
      if (matchingPlatform) {
        // Real platform entry — resolve immediately, no catalog needed.
        setSelectedPlatform(matchingPlatform);
        setIsLegacyCompany(false);
        setLegacyCompanyName('');
      } else {
        // No real platform match. Clear now; the mount/restore effect will
        // resolve to synthetic or legacy once the new catalog fetch settles.
        setSelectedPlatform(EMPTY_PLATFORM);
        setIsLegacyCompany(false);
        setLegacyCompanyName('');
      }
      // Always clear stale program state when org changes.
      setSelectedProgram(EMPTY_PROGRAM);
      setEditProgram(EMPTY_PROGRAM);
      hideProgramDialog();
    } else if (sharedSelectionKey === null && selectedPlatform.key) {
      // Selection was cleared in another tab.
      setSelectedPlatform(EMPTY_PLATFORM);
      setIsLegacyCompany(false);
      setLegacyCompanyName('');
      setSelectedProgram(EMPTY_PROGRAM);
      setEditProgram(EMPTY_PROGRAM);
      hideProgramDialog();
    }
  }, [sharedSelectionKey, selectedPlatform.key]);

  // Memoize filtered course lists for both forms
  const newProgramCourseIds = useMemo(
    () => newProgram.course_list.filter(id => id !== ''),
    [newProgram.course_list],
  );

  const editProgramCourseIds = useMemo(
    () => editProgram.course_list.filter(id => id !== ''),
    [editProgram.course_list],
  );

  /**
   * Active-version IDs for all skill catalog entries.
   * The v2 catalog stores base course keys (no run segment) as keys.
   * The full course list uses active-version IDs (with run), so we must
   * resolve the active version for each skill before passing to CourseListPicker.
   * Inactive skills are excluded.
   */
  const companyCatalogActiveVersionIDs = useMemo(() => {
    const catalog = companyCatalog?.catalog || {};
    return Object.keys(catalog)
      .filter(k => catalog[k]?.type === 'skill')
      .map(k => {
        const versions = catalog[k]?.versions || {};
        const activeEntry = Object.entries(versions).find(([, v]: [string, any]) => v.active);
        return activeEntry ? `${k}${activeEntry[0]}` : null;
      })
      .filter((id): id is string => id !== null);
  }, [companyCatalog]);

  // Course list change handlers for both forms
  const handleNewProgramCourseListChange = useCallback((courseIds: string[]) => {
    setNewProgram((prev) => ({ ...prev, course_list: courseIds }));
  }, []);

  const handleEditProgramCourseListChange = useCallback((courseIds: string[]) => {
    setEditProgram((prev) => ({ ...prev, course_list: courseIds }));
  }, []);

  const handleProgramUpdate = async () => {
    setIsUpdatingProgram(true);
    setUpdateProgramError(null);

    const result: any = await dispatch(updateProgramThunkImport(editProgram));

    if (result.success) {
      setSelectedProgram(result.data);
      setEditProgram(result.data);
      setIsUpdatingProgram(false);
    } else {
      const errorMessage = result.error.response?.error
        || result.error.message
        || 'Failed to update pathway';
      setUpdateProgramError(errorMessage);
      setIsUpdatingProgram(false);
    }
  };

  const handleNewProgramSubmit = async (program: Program) => {
    setIsSubmittingNewProgram(true);
    setNewProgramError(null);

    // eslint-disable-next-line
    const { item_id, ...programData } = program;
    const result: any = await dispatch(createProgramThunk(programData));

    if (result.success) {
      setSelectedProgram(result.data);
      setNewProgram({
        ...EMPTY_PROGRAM,
        org: selectedPlatform.org,
        platform_key: selectedPlatform.key,
      });
      setIsSubmittingNewProgram(false);
      hideProgramDialog();
      // Re-fetch catalog to pick up the pathway catalog entry auto-created by Django signal
      if (companyCatalog?.company?.company_name) {
        dispatch(fetchCompanyCatalog(companyCatalog.company.company_name));
      }
    } else {
      const errorMessage = result.error.response?.error
        || result.error.message
        || 'Failed to create organization';
      setNewProgramError(errorMessage);
      setIsSubmittingNewProgram(false);
    }
  };

  const clearProgramForm = () => {
    setNewProgram({
      ...EMPTY_PROGRAM,
      org: selectedPlatform.org,
      platform_key: selectedPlatform.key,
    });
    setCreateMetaTagInput('');
    setCreateTopicsTagInput('');
  };

  // Tag handlers for create form - Meta
  const handleCreateAddMetaTag = (tag: string) => {
    setNewProgram((prev) => ({
      ...prev,
      tags: {
        ...prev.tags,
        meta: [...prev.tags.meta.filter(t => t !== ''), tag],
      },
    }));
    setCreateMetaTagInput('');
  };

  const handleCreateRemoveMetaTag = (tagToRemove: string) => {
    setNewProgram((prev) => ({
      ...prev,
      tags: {
        ...prev.tags,
        meta: prev.tags.meta.filter(tag => tag !== tagToRemove),
      },
    }));
  };

  // Tag handlers for create form - Topics
  const handleCreateAddTopicsTag = (tag: string) => {
    setNewProgram((prev) => ({
      ...prev,
      tags: {
        ...prev.tags,
        topics: [...prev.tags.topics.filter(t => t !== ''), tag],
      },
    }));
    setCreateTopicsTagInput('');
  };

  const handleCreateRemoveTopicsTag = (tagToRemove: string) => {
    setNewProgram((prev) => ({
      ...prev,
      tags: {
        ...prev.tags,
        topics: prev.tags.topics.filter(tag => tag !== tagToRemove),
      },
    }));
  };

  // Tag handlers for edit form - Meta
  const handleEditAddMetaTag = (tag: string) => {
    setEditProgram((prev) => ({
      ...prev,
      tags: {
        ...prev.tags,
        meta: [...prev.tags.meta.filter(t => t !== ''), tag],
      },
    }));
    setEditMetaTagInput('');
  };

  const handleEditRemoveMetaTag = (tagToRemove: string) => {
    setEditProgram((prev) => ({
      ...prev,
      tags: {
        ...prev.tags,
        meta: prev.tags.meta.filter(tag => tag !== tagToRemove),
      },
    }));
  };

  // Tag handlers for edit form - Topics
  const handleEditAddTopicsTag = (tag: string) => {
    setEditProgram((prev) => ({
      ...prev,
      tags: {
        ...prev.tags,
        topics: [...prev.tags.topics.filter(t => t !== ''), tag],
      },
    }));
    setEditTopicsTagInput('');
  };

  const handleEditRemoveTopicsTag = (tagToRemove: string) => {
    setEditProgram((prev) => ({
      ...prev,
      tags: {
        ...prev.tags,
        topics: prev.tags.topics.filter(tag => tag !== tagToRemove),
      },
    }));
  };

  const handleProgramDelete = async () => {
    // eslint-disable-next-line
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the pathway "${editProgram.name}"? This action cannot be undone.`,
    );

    if (!confirmDelete) {
      return;
    }

    setIsDeletingProgram(true);
    setDeleteProgramError(null);

    const result: any = await dispatch(deleteProgramThunk(selectedPlatform.key, editProgram.item_id));

    if (result.success) {
      setSelectedProgram(EMPTY_PROGRAM);
      setEditProgram(EMPTY_PROGRAM);
      setIsDeletingProgram(false);
      // Refresh the programs list to reflect the deletion
      dispatch(fetchProgramData(selectedPlatform.key));
    } else {
      const errorMessage = result.error.response?.error
        || result.error.message
        || 'Failed to delete pathway';
      setDeleteProgramError(errorMessage);
      setIsDeletingProgram(false);
    }
  };

  if (isLoading) {
    return (
      <Row className="m-0 mt-4 justify-content-center">
        <LoadingSpinner />
      </Row>
    );
  }

  if (isFailed) {
    return (
      <Row className="m-0 mt-4 justify-content-center">
        <Alert variant="danger" icon={Error}>
          {intl.formatMessage(messages.platformsTabErrorMessage)}
        </Alert>
      </Row>
    );
  }

  return (
    <div className="programs-tab-content">
      <div>
        {platforms.length === 0 ? (
          <p>No platforms available.</p>
        ) : (
          <div>
            <div id="platforms-section" style={{ marginBottom: '2rem', marginTop: '2rem' }}>
              <div id="platform-selection" className="col-12" style={{ display: 'flex', justifyContent: 'center', gap: '2rem' }}>
                {/* Searchable, scrollable org selector */}
                <div ref={orgDropdownRef} style={{ position: 'relative', minWidth: '280px' }}>
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      setIsOrgDropdownOpen((prev) => !prev);
                      setOrgSearchQuery('');
                    }}
                    style={{
                      width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                  >
                    <span>{selectedPlatform.name ? selectedPlatform.name : 'Select an organization'}</span>
                    <span style={{ fontSize: '0.75rem', marginLeft: '0.5rem' }}>▼</span>
                  </Button>
                  {isOrgDropdownOpen && (
                    <div
                      className="border rounded bg-white shadow"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1050,
                        marginTop: '4px',
                      }}
                    >
                      {/* Search input */}
                      <div className="p-2 border-bottom" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Search style={{
                          flexShrink: 0, color: '#6c757d', width: '16px', height: '16px',
                        }}
                        />
                        <input
                          // eslint-disable-next-line jsx-a11y/no-autofocus
                          autoFocus
                          type="text"
                          className="form-control form-control-sm border-0 p-0"
                          placeholder="Search organizations..."
                          value={orgSearchQuery}
                          onChange={(e) => setOrgSearchQuery(e.target.value)}
                          style={{ boxShadow: 'none', outline: 'none' }}
                        />
                      </div>
                      {/* Scrollable list */}
                      <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                        {filteredPlatforms.length === 0 ? (
                          <div className="p-2 text-muted" style={{ fontSize: '0.875rem' }}>
                            No organizations match &ldquo;{orgSearchQuery}&rdquo;
                          </div>
                        ) : (
                          filteredPlatforms.map((platform: Platform) => (
                            <div
                              key={platform.key}
                              className="px-3 py-2"
                              style={{
                                cursor: 'pointer',
                                fontSize: '0.9375rem',
                                backgroundColor: selectedPlatform.key === platform.key ? '#e7f3ff' : 'transparent',
                              }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f0f0f0'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = selectedPlatform.key === platform.key ? '#e7f3ff' : 'transparent'; }}
                              onClick={() => {
                                setSelectedPlatform(platform);
                                dispatch(setSelectedCompanyOrPlatformKey(platform.key));
                                setIsLegacyCompany(false);
                                setLegacyCompanyName('');
                                hideProgramDialog();
                                clearProgramForm();
                                setSelectedProgram(EMPTY_PROGRAM);
                                setIsOrgDropdownOpen(false);
                                setOrgSearchQuery('');
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedPlatform(platform);
                                  dispatch(setSelectedCompanyOrPlatformKey(platform.key));
                                  setIsLegacyCompany(false);
                                  setLegacyCompanyName('');
                                  hideProgramDialog();
                                  clearProgramForm();
                                  setSelectedProgram(EMPTY_PROGRAM);
                                  setIsOrgDropdownOpen(false);
                                  setOrgSearchQuery('');
                                }
                              }}
                            >
                              {platform.name}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {isLegacyCompany && (
                <div className="col-12" style={{ marginTop: '1rem' }}>
                  <Alert variant="warning">
                    <Alert.Heading>Company Catalog Required</Alert.Heading>
                    <p>
                      <strong>{legacyCompanyName}</strong> is missing required Company Catalog and platform records.
                      Please contact the development team.
                    </p>
                  </Alert>
                </div>
              )}
              <div id="platform-view-edit-form" className="col-12" style={{ display: selectedPlatform.name && !isMissingPlatform ? '' : 'none', marginTop: '2rem' }}>
                <Form>
                  <p>View the selected organization. Organization and platform details are created automatically.</p>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <Form.Control type="text" floatingLabel="Organization key" value={editPlatform.key} disabled />
                      <Form.Text>Read-only field. Organization key cannot be changed.</Form.Text>
                    </Form.Group>
                    <Form.Group as={Col}>
                      <Form.Control type="text" floatingLabel="Organization" value={editPlatform.org} disabled />
                      <Form.Text>Read-only field. Organization identifier.</Form.Text>
                    </Form.Group>
                    <Form.Group as={Col}>
                      <Form.Control type="text" floatingLabel="Organization display name" value={editPlatform.name} disabled />
                      <Form.Text>A friendly organization name, such as University of Phoenix.</Form.Text>
                    </Form.Group>
                  </Form.Row>
                </Form>
              </div>
            </div>
            <hr className="border-2" />
            {/* Gate the entire programs section behind Company Catalog availability.
                hasCatalog is null while loading — only show the warning once confirmed. */}
            {selectedPlatform.key && !isLegacyCompany && hasCatalog === false && (
              <div className="col-12" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                <Alert variant="warning">
                  <Alert.Heading>Company Catalog Required</Alert.Heading>
                  <p>
                    <strong>{selectedPlatform.name}</strong> has not been migrated to Company Catalog.
                    Please contact the development team to resolve this.
                  </p>
                </Alert>
              </div>
            )}
            {selectedPlatform.key && !isLegacyCompany && isMissingPlatform && (
              <div className="col-12" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                <Alert variant="warning">
                  <Alert.Heading>Platform Record Missing</Alert.Heading>
                  <p>
                    <strong>{selectedPlatform.name}</strong> has a Company Catalog but is missing a platform record.
                    Please contact the development team.
                  </p>
                </Alert>
              </div>
            )}
            <div id="programs-section" style={{ display: (isLegacyCompany || isMissingPlatform || (selectedPlatform.key && hasCatalog === false)) ? 'none' : '' }}>
              <div id="program-selection" className="col-12" style={{ display: 'flex', justifyContent: 'center', gap: '2rem' }}>
                {/* Searchable, scrollable pathway selector */}
                <div ref={pathwayDropdownRef} style={{ position: 'relative', minWidth: '280px' }}>
                  <Button
                    variant="outline-primary"
                    disabled={!selectedPlatform.key || filteredPrograms.length === 0}
                    onClick={() => {
                      setIsPathwayDropdownOpen((prev) => !prev);
                      setPathwaySearchQuery('');
                    }}
                    style={{
                      width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                  >
                    <span>{programProps()}</span>
                    <span style={{ fontSize: '0.75rem', marginLeft: '0.5rem' }}>▼</span>
                  </Button>
                  {isPathwayDropdownOpen && filteredPrograms.length > 0 && (
                    <div
                      className="border rounded bg-white shadow"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1050,
                        marginTop: '4px',
                      }}
                    >
                      {/* Search input */}
                      <div className="p-2 border-bottom" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Search style={{
                          flexShrink: 0, color: '#6c757d', width: '16px', height: '16px',
                        }}
                        />
                        <input
                          // eslint-disable-next-line jsx-a11y/no-autofocus
                          autoFocus
                          type="text"
                          className="form-control form-control-sm border-0 p-0"
                          placeholder="Search pathways..."
                          value={pathwaySearchQuery}
                          onChange={(e) => setPathwaySearchQuery(e.target.value)}
                          style={{ boxShadow: 'none', outline: 'none' }}
                        />
                      </div>
                      {/* Scrollable list */}
                      <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                        {filteredPrograms.length === 0 ? (
                          <div className="p-2 text-muted" style={{ fontSize: '0.875rem' }}>
                            No pathways match &ldquo;{pathwaySearchQuery}&rdquo;
                          </div>
                        ) : (
                          filteredPrograms.map((program: Program) => (
                            <div
                              key={program.item_id}
                              className="px-3 py-2"
                              style={{
                                cursor: 'pointer',
                                fontSize: '0.9375rem',
                                backgroundColor: selectedProgram.item_id === program.item_id ? '#e7f3ff' : 'transparent',
                              }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f0f0f0'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = selectedProgram.item_id === program.item_id ? '#e7f3ff' : 'transparent'; }}
                              onClick={() => {
                                setSelectedProgram(program);
                                hideProgramDialog();
                                setIsPathwayDropdownOpen(false);
                                setPathwaySearchQuery('');
                              }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedProgram(program);
                                  hideProgramDialog();
                                  setIsPathwayDropdownOpen(false);
                                  setPathwaySearchQuery('');
                                }
                              }}
                            >
                              {program.name}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <Button
                  onClick={() => {
                    showProgramDialog();
                    setSelectedProgram(EMPTY_PROGRAM);
                    setNewProgram({
                      ...EMPTY_PROGRAM,
                      org: selectedPlatform.org,
                      platform_key: selectedPlatform.key,
                    });
                  }}
                  disabled={!selectedPlatform.key}
                >
                  + Add new pathway
                </Button>
              </div>
              <div id="program-create-form" className="col-12" style={{ display: isProgramDialogShown ? '' : 'none', marginTop: '2rem' }}>
                <Form>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <Form.Control type="text" floatingLabel="Pathway Display Name" value={newProgram.name} onChange={(e) => setNewProgram((prev) => ({ ...prev, name: e.target.value }))} />
                      <Form.Text>Display name of the pathway. Max length 255 characters.</Form.Text>
                    </Form.Group>
                    <Form.Group as={Col}>
                      <Form.Control type="text" floatingLabel="Associated Platform Organization" value={selectedPlatform.name} disabled />
                      <Form.Text>Associated organization, set by your previous selection</Form.Text>
                    </Form.Group>
                    <Form.Group as={Col}>
                      <Form.Control as="select" floatingLabel="Published" value={String(newProgram.enabled)} disabled>
                        <option value="false">False</option>
                        <option value="true">True</option>
                      </Form.Control>
                      <Form.Text>Published status of the pathway. Pathways are not published on creation.</Form.Text>
                    </Form.Group>
                  </Form.Row>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <Form.Control as="textarea" maxlength="255" floatingLabel="Description" value={newProgram.description} onChange={(e) => setNewProgram((prev) => ({ ...prev, description: e.target.value }))} />
                      <Form.Text>Short description of the pathway. Max length 255 characters.</Form.Text>
                    </Form.Group>
                  </Form.Row>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <TagsChips
                        tags={newProgram.tags.meta}
                        tagInput={createMetaTagInput}
                        onTagInputChange={setCreateMetaTagInput}
                        onAddTag={handleCreateAddMetaTag}
                        onRemoveTag={handleCreateRemoveMetaTag}
                        label="Meta Tags"
                      />
                    </Form.Group>
                  </Form.Row>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <TagsChips
                        tags={newProgram.tags.topics}
                        tagInput={createTopicsTagInput}
                        onTagInputChange={setCreateTopicsTagInput}
                        onAddTag={handleCreateAddTopicsTag}
                        onRemoveTag={handleCreateRemoveTopicsTag}
                        label="Topic Tags"
                      />
                    </Form.Group>
                  </Form.Row>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <CourseListPicker
                        courses={courses}
                        selectedCourseIds={newProgramCourseIds}
                        onCourseListChange={handleNewProgramCourseListChange}
                        isLoading={isLoadingCourses}
                        disabled={false}
                        edxCourseOrg={companyOrg}
                        companyFriendlyName={companyName}
                        companyCatalogIDs={companyCatalogActiveVersionIDs}
                      />
                    </Form.Group>
                  </Form.Row>
                  <Form.Row style={{ justifyContent: 'center' }}>
                    <Button onClick={() => handleNewProgramSubmit(newProgram)}>
                      {isSubmittingNewProgram ? <LoadingSpinner /> : 'Create New Pathway'}
                    </Button>
                  </Form.Row>
                </Form>
                {newProgramError && (
                  <Alert variant="danger" className="mt-3">
                    <strong>Error:</strong> {newProgramError}
                  </Alert>
                )}
              </div>
              <div id="program-view-edit-form" className="col-12" style={{ display: selectedProgram.name ? '' : 'none', marginTop: '2rem' }}>
                <Form>
                  <p>View or edit the selected Pathway. Submit changes with the Submit changes button.</p>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <Form.Control type="text" floatingLabel="item_id" value={editProgram.item_id} disabled />
                      <Form.Text>Read-only field displayed only for reference.</Form.Text>
                    </Form.Group>
                    <Form.Group as={Col}>
                      <Form.Control as="select" floatingLabel="Published" value={String(editProgram.enabled)} onChange={(e) => setEditProgram((prev) => ({ ...prev, enabled: e.target.value === 'true' }))}>
                        <option value="false">False</option>
                        <option value="true">True</option>
                      </Form.Control>
                      <Form.Text>Published status of the pathway. Pathways are not published on creation.</Form.Text>
                    </Form.Group>
                  </Form.Row>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <Form.Control type="text" floatingLabel="Pathway Display Name" value={editProgram.name} onChange={(e) => setEditProgram((prev) => ({ ...prev, name: e.target.value }))} />
                      <Form.Text>Display name of the pathway. Max length 255 characters.</Form.Text>
                    </Form.Group>
                    <Form.Group as={Col}>
                      <Form.Control type="text" floatingLabel="Associated Organization" value={selectedPlatform.name} disabled />
                      <Form.Text>Associated organization, set by your previous selection</Form.Text>
                    </Form.Group>
                  </Form.Row>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <Form.Control as="textarea" maxlength="255" floatingLabel="Description" value={editProgram.description} onChange={(e) => setEditProgram((prev) => ({ ...prev, description: e.target.value }))} />
                      <Form.Text>Short description of the pathway. Max length 255 characters.</Form.Text>
                    </Form.Group>
                  </Form.Row>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <TagsChips
                        tags={editProgram.tags.meta}
                        tagInput={editMetaTagInput}
                        onTagInputChange={setEditMetaTagInput}
                        onAddTag={handleEditAddMetaTag}
                        onRemoveTag={handleEditRemoveMetaTag}
                        label="Meta Tags"
                      />
                    </Form.Group>
                  </Form.Row>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <TagsChips
                        tags={editProgram.tags.topics}
                        tagInput={editTopicsTagInput}
                        onTagInputChange={setEditTopicsTagInput}
                        onAddTag={handleEditAddTopicsTag}
                        onRemoveTag={handleEditRemoveTopicsTag}
                        label="Topic Tags"
                      />
                    </Form.Group>
                  </Form.Row>
                  <Form.Row>
                    <Form.Group as={Col}>
                      <CourseListPicker
                        courses={courses}
                        selectedCourseIds={editProgramCourseIds}
                        onCourseListChange={handleEditProgramCourseListChange}
                        isLoading={isLoadingCourses}
                        disabled={false}
                        edxCourseOrg={companyOrg}
                        companyFriendlyName={companyName}
                        companyCatalogIDs={companyCatalogActiveVersionIDs}
                      />
                    </Form.Group>
                  </Form.Row>
                  <Form.Row style={{ gap: '1rem', justifyContent: 'center' }}>
                    <Button onClick={() => handleProgramUpdate()}>
                      {isUpdatingProgram ? <LoadingSpinner /> : 'Submit Changes'}
                    </Button>
                    <Button variant="danger" onClick={() => handleProgramDelete()}>
                      {isDeletingProgram ? <LoadingSpinner /> : 'Delete'}
                    </Button>
                  </Form.Row>
                  {updateProgramError && (
                    <Alert variant="danger" className="mt-3">
                      <strong>Error:</strong> {updateProgramError}
                    </Alert>
                  )}
                  {deleteProgramError && (
                    <Alert variant="danger" className="mt-3">
                      <strong>Error:</strong> {deleteProgramError}
                    </Alert>
                  )}
                </Form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramsTab;
